import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUserInfo } from '@/features/_data/useUserInfo';
import { IconLoading, IconNoShelf } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { processSortedData } from '@/utils/config';
import { T } from '@/ui/Text/Text';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { Search } from '@/ui/Search/Search';
import { ProductType } from '@/types/ProductType';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { LogStatus } from '@/types/Logging';
import { getWindowLogger } from '@/utils/remoteLogger';
import { useConfigurations } from '../_data/useConfigurations';
import { CreateUnitModal } from './CreateUnitModal/CreateUnitModal';
import { DeleteConfigurationModal } from './DeleteConfigurationModal/DeleteConfigurationModal';
import { DuplicateUnitModal } from './DuplicateUnitModal/DuplicateUnitModal';
import { EditUnitModal } from './EditUnitModal/EditUnitModal';
import {
  StyledConfigsContainer,
  StyledContainer,
  StyledFlex,
  StyledHeader,
  StyledItem,
  StyledLoadingWrapper,
  StyledNoShelfWrapper,
  StyledPageContainer,
  StyledTabs,
} from './styles';
import ConfigCard from './ConfigCard';
import { fsPath } from './__path';

const logger = getWindowLogger({
  file: `${fsPath}/ConfigsHomePage.tsx'`,
});

export const ConfigsHomePage = ({ isAdmin }: { readonly isAdmin: boolean }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const intl = useIntl();
  const [selectedProductType, setSelectedProductType] =
    useState<ProductType | null>(null);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [selectedCustomerId] = useSelectedCustomerId();
  const { data: userInfo, isAdmin: admin } = useUserInfo();
  const [isCreateUnitModalOpen, setIsCreateUnitModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const { data, isLoading, isRefetching } = useConfigurations(
    isAdmin ? selectedCustomerId : userInfo?.customer,
  );
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return processSortedData(data).filter((item) => {
      const matchesSearch = item.companyName
        .toUpperCase()
        .includes(searchPhrase.toUpperCase());
      const matchesProductType =
        !selectedProductType || item.productType === selectedProductType;
      return matchesSearch && matchesProductType;
    });
  }, [data, searchPhrase, selectedProductType]);

  const disableFilters = useMemo(() => {
    // Check if data exists and is an array
    if (!Array.isArray(data)) {
      return false;
    }

    // Check if both "Generic" and "TrihowPad" are present in the data
    const hasGeneric = data.some((item) => item.productType === 'Generic');
    const hasTrihowPad = data.some((item) => item.productType === 'TrihowPad');

    // Return true only if both are present
    return !(hasGeneric && hasTrihowPad);
  }, [data]);

  useEffect(() => {
    if (selectedCustomerId) {
      setSelectedProductType(null);
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    if (admin && !isAdmin) {
      navigate('/admin');
    }
  }, [admin, isAdmin]);

  useEffect(() => {
    logger.debug({
      action: 'configs-home-page',
      status: LogStatus.RENDERED,
    });
  }, []);

  if (isLoading || !data) {
    return (
      <StyledLoadingWrapper>
        <IconLoading />
      </StyledLoadingWrapper>
    );
  }

  return (
    <StyledPageContainer data-testid="configs-home-page-wrapper">
      <StyledHeader>
        <T h1>
          <FormattedMessage defaultMessage="Configurations" id="B+wjm8" />
        </T>
        {isAdmin && (
          <Button
            onClick={() => {
              if (!navigator.onLine) {
                toast({
                  subtitle: intl.formatMessage({
                    defaultMessage:
                      'Please connect to the internet to add a Configuration.',
                    id: 'o/Mt+E',
                  }),
                  mode: TOASTTYPE.No_Connection,
                });
                return;
              }

              setIsCreateUnitModalOpen(true);
            }}
            size="large"
          >
            <FormattedMessage defaultMessage="Create New" id="CVm3PB" />
          </Button>
        )}
      </StyledHeader>

      {data?.length ? (
        <StyledContainer>
          <StyledHeader disableFilters={disableFilters}>
            {!disableFilters && (
              <StyledTabs>
                <StyledItem
                  onClick={() => setSelectedProductType(null)}
                  active={selectedProductType === null}
                >
                  All
                </StyledItem>
                <StyledItem
                  active={selectedProductType === ProductType.Generic}
                  onClick={() => setSelectedProductType(ProductType.Generic)}
                >
                  Standard
                </StyledItem>
                <StyledItem
                  active={selectedProductType === ProductType.TrihowPad}
                  onClick={() => setSelectedProductType(ProductType.TrihowPad)}
                >
                  Trihow-Pad
                </StyledItem>
              </StyledTabs>
            )}

            <Search
              testId="config-search"
              placeholder={`${intl.formatMessage({
                defaultMessage: 'Search for config',
                id: 'mbyhxM',
              })} …`}
              value={searchPhrase}
              setValue={setSearchPhrase}
            />
          </StyledHeader>

          <StyledConfigsContainer>
            {isRefetching && process.env.NODE_ENV !== 'test' ? (
              <StyledLoadingWrapper>
                <IconLoading />
              </StyledLoadingWrapper>
            ) : (
              <>
                {filteredData.length ? (
                  filteredData
                    .sort(
                      (a, b) =>
                        new Date(b.changedAt ?? 0).valueOf() -
                        new Date(a.changedAt ?? 0).valueOf(),
                    )
                    .map((item, index) => {
                      return (
                        <ConfigCard
                          key={item.unitId}
                          item={item}
                          index={index}
                          isAdmin={isAdmin}
                          setSelectedUnitId={setSelectedUnitId}
                          setIsEditModalOpen={setIsEditModalOpen}
                          setDuplicateModalOpen={setDuplicateModalOpen}
                          setIsDeleteConfirmationModalOpen={
                            setIsDeleteConfirmationModalOpen
                          }
                        />
                      );
                    })
                ) : (
                  <Flex justify="center">
                    <FormattedMessage
                      defaultMessage="no configs found"
                      id="L2jpcW"
                    />
                    &nbsp;…
                  </Flex>
                )}
              </>
            )}
          </StyledConfigsContainer>
        </StyledContainer>
      ) : (
        <StyledNoShelfWrapper
          align="center"
          direction="column"
          gap={32}
          justify="center"
        >
          <IconNoShelf />
          <StyledFlex direction="column" gap={12}>
            <T h1>
              <FormattedMessage
                defaultMessage="No Configurations"
                id="BBsEo3"
              />
            </T>
            <T body1>
              <FormattedMessage
                defaultMessage="Please come back later"
                id="7Lhi2X"
              />
            </T>
          </StyledFlex>
        </StyledNoShelfWrapper>
      )}

      <CreateUnitModal
        isModalOpen={isCreateUnitModalOpen}
        setIsModalOpen={setIsCreateUnitModalOpen}
      />

      <EditUnitModal
        unitId={selectedUnitId ?? ''}
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
      />

      <DuplicateUnitModal
        unitId={selectedUnitId ?? ''}
        isModalOpen={duplicateModalOpen}
        setIsModalOpen={setDuplicateModalOpen}
      />

      <DeleteConfigurationModal
        unitId={selectedUnitId ?? ''}
        isModalOpen={isDeleteConfirmationModalOpen}
        setIsModalOpen={setIsDeleteConfirmationModalOpen}
      />
    </StyledPageContainer>
  );
};
